import {
    type FilterChip,
    type FilterOperator,
    type FilterOption,
} from '@apps/web/components/ui/filter_bar';
import { isRecipientBlockCode } from '@apps/web/lib/blocked_phone_utils';
import { readConversationOwnership } from '@apps/web/swr_data_fetching/conversation_ownership';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

export const INBOX_UNASSIGNED_ASSIGNEE_VALUE = 'unassigned';
export const CAREGIVER_TAGS_DIMENSION_KEY = 'caregiver_tags';
export const CAREGIVER_TAGS_DIMENSION_LABEL = 'Caregiver tags';

export function inboxAssigneeFilterOptions(
    conversations: ReadonlyArray<OrganizationConversationMetadata>,
    knownAssignees: ReadonlyArray<{
        readonly value: string;
        readonly label: string;
    }> = [],
    includeUnassigned = false,
): Array<FilterOption> {
    const assigned = new Map<string, { label: string; count: number }>();
    for (const assignee of knownAssignees) {
        assigned.set(assignee.value, { label: assignee.label, count: 0 });
    }
    let unassignedCount = 0;
    for (const conversation of conversations) {
        const ownership = readConversationOwnership(conversation);
        if (ownership.assigned_to_user_id === null) {
            unassignedCount += 1;
            continue;
        }
        const existing = assigned.get(ownership.assigned_to_user_id);
        if (existing === undefined) {
            assigned.set(ownership.assigned_to_user_id, {
                label: ownership.assigned_to_user_name ?? 'Unknown',
                count: 1,
            });
        } else {
            existing.count += 1;
        }
    }
    const options = [...assigned.entries()]
        .map(([value, option]) => ({
            value,
            label: option.label,
            ...(option.count > 0 ? { count: option.count } : {}),
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
    if (unassignedCount > 0 || includeUnassigned) {
        options.push({
            value: INBOX_UNASSIGNED_ASSIGNEE_VALUE,
            label: 'Unassigned',
            ...(unassignedCount > 0 ? { count: unassignedCount } : {}),
        });
    }
    return options;
}

export function shouldContinueLoadingInboxPages({
    hasClientSideFilters,
    isLoadingMore,
    isReachingEnd,
}: {
    readonly hasClientSideFilters: boolean;
    readonly isLoadingMore: boolean;
    readonly isReachingEnd: boolean;
}): boolean {
    return hasClientSideFilters && !isLoadingMore && !isReachingEnd;
}

export function conversationMatchesAssigneeFilter(
    conversation: OrganizationConversationMetadata,
    selectedAssigneeIds: ReadonlyArray<string>,
): boolean {
    if (selectedAssigneeIds.length === 0) {
        return true;
    }
    const ownership = readConversationOwnership(conversation);
    const assigneeId =
        ownership.assigned_to_user_id ?? INBOX_UNASSIGNED_ASSIGNEE_VALUE;
    return selectedAssigneeIds.includes(assigneeId);
}

export function caregiverTagChipValues(
    chips: ReadonlyArray<FilterChip>,
    operator: FilterOperator,
): ReadonlyArray<string> {
    return (
        chips.find(
            (chip) =>
                chip.dimension === CAREGIVER_TAGS_DIMENSION_KEY &&
                (chip.operator ?? 'is') === operator,
        )?.values ?? []
    );
}

export function caregiverTagFilterOptions(
    tags: ReadonlyArray<string>,
): Array<FilterOption> {
    const unique = new Set<string>();
    for (const tag of tags) {
        if (tag !== '') {
            unique.add(tag);
        }
    }
    return [...unique]
        .map((value) => ({ value, label: value }))
        .sort((left, right) => left.label.localeCompare(right.label));
}

function caregiverHasAnyTag(
    caregiverTags: ReadonlyArray<string>,
    selectedTags: ReadonlyArray<string>,
): boolean {
    const caregiverTagSet = new Set(caregiverTags);
    return selectedTags.some((tag) => caregiverTagSet.has(tag));
}

export function conversationMatchesCaregiverTagFilter(
    conversation: Pick<OrganizationConversationMetadata, 'caregiver_tags'>,
    includeTags: ReadonlyArray<string>,
    excludeTags: ReadonlyArray<string>,
): boolean {
    if (includeTags.length === 0 && excludeTags.length === 0) {
        return true;
    }
    const caregiverTags = conversation.caregiver_tags ?? [];
    if (
        includeTags.length > 0 &&
        !caregiverHasAnyTag(caregiverTags, includeTags)
    ) {
        return false;
    }
    if (
        excludeTags.length > 0 &&
        caregiverHasAnyTag(caregiverTags, excludeTags)
    ) {
        return false;
    }
    return true;
}

export function shouldShowInboxRecencyFooter(search: string): boolean {
    return search.length === 0;
}

export function useInboxCaregiverTagVocabulary(
    organizationId: string | null,
): ReadonlyArray<string> {
    const { data } = useApiQuery({
        path: '/organizations/{organization_id}/caregivers/filter-options',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {},
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'conversations-inbox-caregiver-tags',
    });
    return data?.tags ?? [];
}

export const DELIVERY_DIMENSION_KEY = 'delivery';
export const DELIVERY_DIMENSION_LABEL = 'Delivery status';

export type InboxDeliveryState = 'delivered' | 'blocked' | 'failed';

export const INBOX_DELIVERY_OPTIONS: ReadonlyArray<FilterOption> = [
    { value: 'delivered', label: 'Delivered' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'failed', label: 'Failed to send' },
];

/**
 * How the thread's newest message ended up, or null when it says nothing
 * about delivery -- an inbound message, one still pending, or one sent
 * without a carrier receipt yet.
 *
 * This reads only `last_message`, which is all the list payload carries. A
 * thread whose earlier text failed but that has since been replied to does
 * not match; answering that properly needs the filter to run in the API.
 */
export function conversationDeliveryState(
    conversation: OrganizationConversationMetadata,
): InboxDeliveryState | null {
    const lastMessage = conversation.last_message;
    if (!lastMessage || lastMessage.role !== 'assistant') {
        return null;
    }
    if (lastMessage.sent_status === 'failed') {
        const errorCode = lastMessage.error_code;
        return errorCode !== null &&
            errorCode !== undefined &&
            isRecipientBlockCode(String(errorCode))
            ? 'blocked'
            : 'failed';
    }
    // "Sent" alone is Phoebe handing the text to Twilio. Only the carrier's
    // receipt says it reached the phone, so nothing else counts as delivered.
    return lastMessage.provider_status === 'delivered' ? 'delivered' : null;
}

export function conversationMatchesDeliveryFilter(
    conversation: OrganizationConversationMetadata,
    selectedStates: ReadonlyArray<string>,
): boolean {
    if (selectedStates.length === 0) {
        return true;
    }
    const state = conversationDeliveryState(conversation);
    return state !== null && selectedStates.includes(state);
}
